import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import StarterUpgrade from "../components/StarterUpgrade";
import { useAuthUser } from "../lib/useAuthUser";
import { useVendorFlags } from "../lib/useVendorFlags";
import { startGoogleLogin, vendorMe, vendorPortal, vendorSaveProfile, vendorUploadImage, vendorCaptionSuggest, vendorBadges, vendorCheckout, vendorClaimRelease, API } from "../lib/api";
import SponsorPanel from "../components/SponsorPanel";

export default function Vendor() {
  const { user, loading: authLoading } = useAuthUser();
  const { flags, loading: flagsLoading } = useVendorFlags();
  const [me, setMe] = useState(null);
  const [sp] = useSearchParams();
  const upgradeSlug = sp.get("upgrade");
  const upgradePlan = sp.get("plan");
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (user && flags.claiming) vendorMe().then(setMe).catch(() => setMe({ tools: [], pending_claims: [] }));
  }, [user, flags.claiming]);

  useEffect(() => {
    if (!me || !upgradeSlug || upgrading || !["starter_monthly", "starter_yearly"].includes(upgradePlan)) return;
    const t = me.tools.find((x) => x.slug === upgradeSlug);
    if (!t) return;
    if (t.plan === "starter" && t.plan_status === "active") {
      toast.message("this listing is already on Starter");
      return;
    }
    setUpgrading(true);
    vendorCheckout(upgradeSlug, upgradePlan)
      .then((r) => { window.location.href = r.checkout_url; })
      .catch((e) => { toast.error(e?.response?.data?.detail || "could not start checkout"); setUpgrading(false); });
  }, [me, upgradeSlug, upgradePlan, upgrading]);

  return (
    <div className="min-h-screen bg-white text-black font-mono">
      <Seo title="vendor dashboard" description="Manage your claimed listings" path="/vendor" noindex />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10 text-[13px]" data-testid="vendor-page">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ vendor dashboard</h1>
        {flagsLoading || authLoading ? null : !flags.claiming ? (
          <p className="mt-4" data-testid="vendor-off">that page isn&apos;t available.</p>
        ) : !user ? (
          <div className="mt-4" data-testid="vendor-login">
            <p>sign in to see the listings you&apos;ve claimed.</p>
            <button type="button" className="tv-btn tv-btn-primary mt-2" onClick={() => startGoogleLogin("/vendor" + window.location.search)} data-testid="vendor-login-btn">[sign in with Google]</button>
          </div>
        ) : !me ? (
          <p className="mt-4 text-[#888]">loading…</p>
        ) : upgrading ? (
          <p className="mt-4" data-testid="vendor-upgrading">taking you to secure checkout for <b>{upgradeSlug}</b> ({upgradePlan === "starter_yearly" ? "$495 first year" : "$49/month first year"})…</p>
        ) : (
          <>
            {me.tools.length === 0 && me.pending_claims.length === 0 && (
              <p className="mt-4" data-testid="vendor-empty">
                you haven&apos;t claimed a listing yet. Find your tool and click &quot;claim this listing&quot; at the bottom of its page, or{" "}
                <Link to="/for-vendors" className="tv-link">read how it works</Link>.
              </p>
            )}
            {me.pending_claims.map((c) => (
              <div key={c.id} className="mt-4 border border-dashed border-black p-3" data-testid={`vendor-pending-${c.slug}`}>
                claim for <b>{c.slug}</b> is waiting for verification → <Link to={`/claim/${c.slug}`} className="tv-link">finish it</Link>
              </div>
            ))}
            <div className="mt-6 space-y-10">
              {me.tools.map((t) => <ClaimedTool key={t.slug} tool={t} starterOn={flags.starter} badgesOn={flags.badges} sponsoredOn={flags.sponsored} onChanged={() => vendorMe().then(setMe).catch(() => {})} />)}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ClaimedTool({ tool, starterOn, badgesOn, sponsoredOn, onChanged }) {
  const active = tool.plan === "starter" && tool.plan_status === "active";
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const portal = async () => {
    setBusy(true);
    try {
      const r = await vendorPortal(tool.slug);
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not open billing");
      setBusy(false);
    }
  };
  const release = async () => {
    if (!window.confirm(`Release your claim on ${tool.title}? You'll lose the owner tools until you claim it again. This won't delete the listing.`)) return;
    setReleasing(true);
    try {
      await vendorClaimRelease(tool.slug);
      toast.success(`released your claim on ${tool.title}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not release claim");
      setReleasing(false);
    }
  };
  return (
    <section className="border border-black p-4" data-testid={`vendor-tool-${tool.slug}`}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <Link to={`/tool/${tool.slug}`} className="tv-link font-bold text-[15px]">{tool.title}</Link>
        <span className="border border-black px-1 text-[11px]" data-testid={`vendor-plan-${tool.slug}`}>
          {active ? `★ starter · ${tool.plan_interval === "year" ? "yearly" : "monthly"}` : "free · claimed ✓"}
        </span>
        {active && (
          <button type="button" className="tv-link underline text-[12px]" disabled={busy} onClick={portal} data-testid={`vendor-billing-${tool.slug}`}>
            manage billing
          </button>
        )}
        <Link to={`/claim/${tool.slug}`} className="tv-link underline text-[12px]">request a correction</Link>
        <button type="button" className="tv-link underline text-[12px] text-[#b91c1c]" disabled={releasing} onClick={release} data-testid={`vendor-release-${tool.slug}`}>
          {releasing ? "releasing…" : "release claim"}
        </button>
      </div>
      {starterOn && !active && <div className="mt-4"><StarterUpgrade slug={tool.slug} plan={tool.plan} /></div>}
      {active && <ProfileEditor tool={tool} />}
      {badgesOn && <Badges slug={tool.slug} />}
      {sponsoredOn && <SponsorPanel slug={tool.slug} />}
    </section>
  );
}

const CTA_TYPES = [
  { v: "signup", label: "Start / sign up free", def: "Start free trial" },
  { v: "website", label: "Visit website", def: "Visit website" },
  { v: "demo", label: "Request a demo", def: "Request a demo" },
  { v: "meeting", label: "Schedule a meeting", def: "Schedule a meeting" },
  { v: "pricing", label: "See pricing", def: "See pricing" },
  { v: "contact", label: "Contact / sales", def: "Contact us" },
];
const CTA_DEFAULTS = Object.fromEntries(CTA_TYPES.map((t) => [t.v, t.def]));

function ProfileEditor({ tool }) {
  const d = tool.vendor_draft || tool.vendor_live || {};
  const [f, setF] = useState({
    video_url: d.video_url || "", positioning: d.positioning || "",
    integrations: (d.integrations || []).join(", "),
  });
  const initCtas = (d.ctas && d.ctas.length)
    ? d.ctas.map((c) => ({ type: c.type || "website", label: c.label || "", url: c.url || "" }))
    : (d.cta_url ? [{ type: "website", label: d.cta_label || "", url: d.cta_url }] : []);
  const [ctas, setCtas] = useState([
    initCtas[0] || { type: "signup", label: "", url: "" },
    initCtas[1] || { type: "pricing", label: "", url: "" },
  ]);
  const [shots, setShots] = useState(() =>
    (d.screenshots || [])
      .map((s) => (typeof s === "string" ? { url: s, caption: "" } : { ...s, url: s?.url || "", caption: s?.caption || "" }))
      .filter((s) => s.url)
  );
  const [logo, setLogo] = useState(d.logo || "");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(tool.vendor_draft?.status);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setCta = (i, patch) => setCtas((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const setCtaType = (i) => (e) => {
    const type = e.target.value;
    setCtas((prev) => prev.map((c, idx) => {
      if (idx !== i) return c;
      const wasDefault = !c.label || Object.values(CTA_DEFAULTS).includes(c.label);
      return { ...c, type, label: wasDefault ? CTA_DEFAULTS[type] : c.label };
    }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const cleanCtas = ctas
        .filter((c) => c.url.trim() || c.type === "demo" || c.type === "meeting")
        .map((c) => ({ type: c.type, label: (c.label.trim() || CTA_DEFAULTS[c.type]), url: c.url.trim() }));
      const r = await vendorSaveProfile({
        slug: tool.slug, video_url: f.video_url, positioning: f.positioning,
        integrations: f.integrations.split(",").map((s) => s.trim()).filter(Boolean),
        screenshots: shots, logo: logo || null, ctas: cleanCtas,
      });
      setStatus(r.vendor.status);
      toast.success("submitted for review — live once approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save");
    } finally {
      setBusy(false);
    }
  };
  const inp = "mt-1 w-full border border-black p-1.5 text-[13px] font-mono";
  return (
    <div className="mt-4" data-testid={`vendor-profile-${tool.slug}`}>
      <h3 className="text-[14px] font-bold border-b border-[#ddd] pb-1">your page</h3>
      {status && (
        <p className="text-[12px] mt-1" data-testid={`vendor-profile-status-${tool.slug}`}>
          {status === "pending" ? "⏳ changes waiting for review" : status === "approved" ? "✓ live" : `✗ rejected${tool.vendor_draft?.note ? ` — ${tool.vendor_draft.note}` : ""}`}
        </p>
      )}

      <div className="mt-3">
        <div className="text-[12px] font-bold">call-to-action buttons</div>
        <p className="text-[11px] text-[#666] mt-0.5">
          Your listing shows a primary button and an optional second one.
        </p>
        <CtaRow i={0} role="main button" cta={ctas[0]} inp={inp} setCta={setCta} setCtaType={setCtaType} />
        <CtaRow i={1} role="second button (optional)" cta={ctas[1]} inp={inp} setCta={setCta} setCtaType={setCtaType} />
      </div>

      <label className="block mt-4 text-[12px]">video URL (YouTube / Loom)<input className={inp} value={f.video_url} onChange={set("video_url")} placeholder="https://…" data-testid="vp-video" /></label>
      <label className="block mt-3 text-[12px]">why us vs the alternatives (≤600 chars, factual — we review it)<textarea className={inp} rows={3} maxLength={600} value={f.positioning} onChange={set("positioning")} data-testid="vp-positioning" /></label>
      <label className="block mt-3 text-[12px]">integrations (comma-separated)<input className={inp} value={f.integrations} onChange={set("integrations")} placeholder="Interactive Brokers, Excel, Slack" data-testid="vp-integrations" /></label>
      <div className="mt-3 text-[12px]">logo</div>
      <ImgUploader value={logo} onChange={setLogo} multiple={false} testid="vp-logo" />
      <div className="mt-3 text-[12px]">screenshots (up to 6) — add a short description under each</div>
      <ShotEditor shots={shots} setShots={setShots} max={6} title={tool.title} />
      <button type="button" className="tv-btn tv-btn-primary mt-3" disabled={busy} onClick={save} data-testid="vp-save">{busy ? "…" : "[submit for review]"}</button>
    </div>
  );
}

function CtaRow({ i, role, cta, inp, setCta, setCtaType }) {
  return (
    <div className="mt-2 border border-[#ddd] p-2" data-testid={`vp-cta-row-${i}`}>
      <div className="text-[11px] uppercase tracking-wide text-[#888]">{role}</div>
      <div className="flex flex-col sm:flex-row gap-2 mt-1">
        <label className="text-[12px] sm:w-44">
          type
          <select className={inp} value={cta.type} onChange={setCtaType(i)} data-testid={`vp-cta-type-${i}`}>
            {CTA_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label className="text-[12px] flex-1">
          button text
          <input className={inp} value={cta.label} onChange={(e) => setCta(i, { label: e.target.value })} placeholder={CTA_DEFAULTS[cta.type]} maxLength={40} data-testid={`vp-cta-label-${i}`} />
        </label>
      </div>
      {cta.type === "demo" || cta.type === "meeting" ? (
        <p className="text-[11px] text-[#888] mt-2 mb-0" data-testid={`vp-cta-form-note-${i}`}>
          Opens a built-in request form on your page — no link needed. Each request is emailed to you instantly.
        </p>
      ) : (
        <label className="block text-[12px] mt-1">
          link (https://… or mailto:… for contact)
          <input className={inp} value={cta.url} onChange={(e) => setCta(i, { url: e.target.value })} placeholder={cta.type === "contact" ? "mailto:sales@yoursite.com" : "https://…"} data-testid={`vp-cta-url-${i}`} />
        </label>
      )}
    </div>
  );
}

function ShotEditor({ shots, setShots, max = 6, title }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState({});
  const suggest = async (url, force) => {
    setSuggesting((s) => ({ ...s, [url]: true }));
    try {
      const { caption } = await vendorCaptionSuggest(url, title);
      if (caption) setShots((prev) => prev.map((s) => (s.url === url && (force || !s.caption) ? { ...s, caption } : s)));
    } catch {
      /* best-effort — leave caption blank */
    } finally {
      setSuggesting((s) => { const n = { ...s }; delete n[url]; return n; });
    }
  };
  const add = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type && f.type.startsWith("image/"));
    if (files.length === 0) { toast.error("images only (PNG, JPG, WebP)"); return; }
    const room = max - shots.length;
    if (room <= 0) { toast.error(`you already have ${max} screenshots — remove one first`); return; }
    setBusy(true);
    try {
      for (const file of files.slice(0, room)) {
        const { url } = await vendorUploadImage(file);
        setShots((prev) => (prev.length >= max ? prev : [...prev, { url, caption: "" }]));
        suggest(url, false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "upload failed — please try again");
    } finally {
      setBusy(false);
    }
  };
  const setCaption = (i, v) => setShots((prev) => prev.map((s, idx) => (idx === i ? { ...s, caption: v } : s)));
  const remove = (i) => setShots((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setShots((prev) => {
    const a = [...prev]; const j = i + dir;
    if (j < 0 || j >= a.length) return prev;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const mini = "border border-black px-1.5 py-0.5 text-[11px] disabled:opacity-30 hover:bg-black hover:text-white";
  return (
    <div data-testid="vp-screenshots">
      {shots.length < max && (
        <div
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
          onClick={() => document.getElementById("vp-shots-inp").click()}
          className={"mt-1 border border-dashed p-3 text-[12px] text-center cursor-pointer " + (over ? "border-black bg-[#FBF8EE]" : "border-[#999] hover:border-black")}
          data-testid="vp-screenshots-drop"
        >
          {busy ? "uploading…" : "drag an image here, or click to choose — we'll suggest a description you can edit"}
          <input id="vp-shots-inp" type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </div>
      )}
      {shots.map((s, i) => (
        <div key={`${s.url}-${i}`} className="mt-2 border border-black p-2 flex gap-2" data-testid={`vp-shot-${i}`}>
          <img src={s.url} alt="" className="h-16 w-24 object-cover border border-[#ccc] bg-[#fafafa] shrink-0" />
          <div className="flex-1 min-w-0">
            <input
              className="w-full border border-black p-1.5 text-[13px] font-mono"
              placeholder={suggesting[s.url] ? "suggesting a description…" : "describe what this screenshot shows (≤200 chars)"}
              maxLength={200}
              value={s.caption}
              onChange={(e) => setCaption(i, e.target.value)}
              data-testid={`vp-shot-caption-${i}`}
            />
            <div className="mt-1 flex gap-1.5 items-center">
              <button type="button" className={mini} onClick={() => move(i, -1)} disabled={i === 0} data-testid={`vp-shot-up-${i}`} aria-label="move up">↑</button>
              <button type="button" className={mini} onClick={() => move(i, 1)} disabled={i === shots.length - 1} data-testid={`vp-shot-down-${i}`} aria-label="move down">↓</button>
              <button type="button" className={mini} onClick={() => suggest(s.url, true)} disabled={!!suggesting[s.url]} data-testid={`vp-shot-suggest-${i}`}>
                {suggesting[s.url] ? "…" : "✦ suggest"}
              </button>
              <button type="button" className="border border-[#b91c1c] text-[#b91c1c] px-1.5 py-0.5 text-[11px] hover:bg-[#b91c1c] hover:text-white" onClick={() => remove(i)} data-testid={`vp-shot-remove-${i}`}>remove</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ImgUploader({ value, onChange, multiple = false, max = 6, testid }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = multiple ? (value || []) : (value ? [value] : []);
  const add = async (files) => {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) { toast.error("images only"); continue; }
        const { url } = await vendorUploadImage(file);
        if (multiple) onChange((prev) => [...prev, url].slice(0, max));
        else onChange(url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "upload failed");
    } finally {
      setBusy(false);
    }
  };
  const remove = (u) => multiple ? onChange((prev) => prev.filter((x) => x !== u)) : onChange("");
  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        onClick={() => document.getElementById(`${testid}-inp`).click()}
        className={"mt-1 border border-dashed p-3 text-[12px] text-center cursor-pointer " + (over ? "border-black bg-[#FBF8EE]" : "border-[#999] hover:border-black")}
        data-testid={`${testid}-drop`}
      >
        {busy ? "uploading…" : "drag an image here, or click to choose"}
        <input id={`${testid}-inp`} type="file" accept="image/*" multiple={multiple} className="hidden"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      {list.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {list.map((u, i) => (
            <div key={`${u}-${i}`} className="relative border border-black" data-testid={`${testid}-item-${i}`}>
              <img src={u} alt="" className="h-16 w-24 object-cover block bg-[#fafafa]" />
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(u); }}
                className="absolute top-0 right-0 bg-black text-white text-[11px] leading-none px-1 py-0.5" data-testid={`${testid}-remove-${i}`}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Badges({ slug }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    vendorBadges(slug).then((r) => setList(r.badges)).catch(() => setList([]));
  }, [slug]);
  if (!list?.length) return null;
  return (
    <div className="mt-4" data-testid={`vendor-badges-${slug}`}>
      <h3 className="text-[14px] font-bold border-b border-[#ddd] pb-1">badges you&apos;ve earned</h3>
      <div className="mt-2 flex flex-wrap gap-3">
        {list.map((b) => {
          const src = `${API}/badges/${slug}/rank.svg?key=${encodeURIComponent(b.key)}`;
          const snippet = `<a href="https://terminalvalue.io/tool/${slug}"><img src="${src}" alt="${b.line1} on TerminalValue.io" width="230" height="54"></a>`;
          return (
            <div key={b.key} className="text-[11px]">
              <img src={src} alt={b.line1} width={230} height={54} />
              <button type="button" className="tv-link underline mt-1" onClick={() => navigator.clipboard?.writeText(snippet).then(() => toast.success("embed code copied"))} data-testid={`badge-copy-${slug}-${b.key}`}>
                copy embed code
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
